// Route-level SEO helper. Updates <title>, <meta>, canonical, OG, Twitter and JSON-LD.

const SITE_URL = "https://eclipsecubiertas.com.mx";

function setMetaByName(name, content) {
    if (!content) return;
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setMetaByProperty(prop, content) {
    if (!content) return;
    let el = document.querySelector(`meta[property="${prop}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLinkRel(rel, href) {
    if (!href) return;
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function setJSONLD(id, data) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
}

export function applySEO({ path = "/", title, description, keywords, ogImage, ogImageAlt, ogType = "website", noindex = false, jsonLD = [] } = {}) {
    const url = `${SITE_URL}${path}`;
    if (title) document.title = title;
    setMetaByName("description", description);
    setMetaByName("keywords", keywords);
    setMetaByName(
        "robots",
        noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    setMetaByName("googlebot", noindex ? "noindex, nofollow" : "index, follow");
    setMetaByName("bingbot", noindex ? "noindex, nofollow" : "index, follow");
    setMetaByName("author", "Eclipse Cubiertas SA de CV");
    setMetaByName("geo.region", "MX-NLE");
    setMetaByName("geo.placename", "Santa Catarina, Nuevo León");
    setMetaByName("geo.position", "25.6726;-100.4583");
    setMetaByName("ICBM", "25.6726, -100.4583");
    setMetaByName("language", "es-MX");
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", url);
    setMetaByProperty("og:type", ogType);
    setMetaByProperty("og:locale", "es_MX");
    setMetaByProperty("og:site_name", "Eclipse Cubiertas");
    if (ogImage) {
        const absImg = ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`;
        setMetaByProperty("og:image", absImg);
        setMetaByProperty("og:image:width", "1264");
        setMetaByProperty("og:image:height", "848");
        if (ogImageAlt) setMetaByProperty("og:image:alt", ogImageAlt);
        setMetaByName("twitter:image", absImg);
    }
    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setLinkRel("canonical", url);

    // Remove old JSON-LD blocks generated by us (id prefix "ld-")
    Array.from(document.querySelectorAll('script[type="application/ld+json"][id^="ld-route-"]'))
        .forEach((n) => n.remove());

    jsonLD.forEach((data, i) => setJSONLD(`ld-route-${i}`, data));
}

// Pre-built schemas
export const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#org`,
    "name": "Eclipse Cubiertas SA de CV",
    "url": SITE_URL,
    "logo": `${SITE_URL}/brand/eclipse_logo.png`,
    "email": "info@eclipsecubiertas.com",
    "telephone": "+52-81-8989-9822",
    "foundingDate": "2005",
    "areaServed": "MX",
    "sameAs": [],
    "contactPoint": [{
        "@type": "ContactPoint",
        "telephone": "+52-81-8989-9822",
        "email": "info@eclipsecubiertas.com",
        "contactType": "sales",
        "areaServed": "MX",
        "availableLanguage": ["Spanish"],
    }],
};

export const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#local`,
    "name": "Eclipse Cubiertas",
    "legalName": "Eclipse Cubiertas SA de CV",
    "image": `${SITE_URL}/brand/eclipse_logo.png`,
    "url": SITE_URL,
    "telephone": "+52-81-8989-9822",
    "email": "info@eclipsecubiertas.com",
    "priceRange": "$$$",
    "address": {
        "@type": "PostalAddress",
        "streetAddress": "Calle San Pedro No. 157, Bodega No. 8, Extora Park Flex Space",
        "addressLocality": "Santa Catarina",
        "addressRegion": "Nuevo León",
        "postalCode": "66367",
        "addressCountry": "MX",
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 25.687958, "longitude": -100.469647 },
    "hasMap": "https://maps.app.goo.gl/7fqaFjJ1Mn2QLMz77",
    "openingHoursSpecification": [{
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00",
    }],
    "sameAs": ["https://www.youtube.com/@eclipsegrupo"],
};

export function breadcrumbSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": items.map((it, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": it.name,
            "item": `${SITE_URL}${it.path}`,
        })),
    };
}

export function productSchema({ name, description, image, brand = "Eclipse Cubiertas", sku, priceRange = "$$$", rating = 4.8, reviewCount = 87 }) {
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": name,
        "description": description,
        "image": image,
        "brand": { "@type": "Brand", "name": brand },
        "sku": sku,
        "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "MXN",
            "lowPrice": "1500",
            "highPrice": "8500",
            "offerCount": "100",
            "availability": "https://schema.org/InStock",
            "areaServed": "MX",
            "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "MXN" },
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": String(rating),
            "reviewCount": String(reviewCount),
        },
    };
}

export function faqSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": items.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
    };
}

export function videoObjectSchema({ name, description, videoId, uploadDate = "2024-01-01" }) {
    return {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": name,
        "description": description || name,
        "thumbnailUrl": [
            `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
            `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
        ],
        "uploadDate": uploadDate,
        "contentUrl": `https://www.youtube.com/watch?v=${videoId}`,
        "embedUrl": `https://www.youtube.com/embed/${videoId}`,
        "publisher": {
            "@type": "Organization",
            "name": "Eclipse Cubiertas",
            "logo": {
                "@type": "ImageObject",
                "url": `${SITE_URL}/brand/eclipse_logo.png`,
            },
        },
    };
}

export const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "url": SITE_URL,
    "name": "Eclipse Cubiertas",
    "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_URL}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
    },
};
